import { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { getLicenseId } from 'services/DefaultService';
import { Permissions } from 'services/enums';
import ProtectedRoute from './ProtectedRoute';
import { AnyOfPermissions, IRouteProps } from './types';

export const isRoutePermitted = (
	route: IRouteProps,
	allowed: (permission?: Permissions | Permissions[] | AnyOfPermissions) => boolean,
	licenseId?: string | null,
) => {
	if (route.requiredLicenseId && route.requiredLicenseId !== licenseId) return false;
	return route.permissionMatch === 'any' && Array.isArray(route.permissionId)
		? route.permissionId.some(permission => allowed(permission))
		: allowed(route.permissionId);
};

export const getRoutes = (
	allRoutes: IRouteProps[],
	isLogged: boolean,
	isRouteAllowed: (permission?: Permissions | Permissions[] | AnyOfPermissions) => boolean,
	fallbackRoute: string,
): ReactElement[] =>
	allRoutes.flatMap((route: IRouteProps) => {
		if (route.collapse) {
			return getRoutes(route.collapse, isLogged, isRouteAllowed, fallbackRoute);
		}

		if (!route.route) {
			return [];
		}

		const routeElement = (
			<Route path={route.matchPath ?? route.route} element={route.component} key={route.key} />
		);

		if (!route.protected) {
			return routeElement;
		}

		return (
			<Route
				element={
					<ProtectedRoute
						isLogged={isLogged}
						isPermitted={isRoutePermitted(route, isRouteAllowed, getLicenseId())}
						fallbackRoute={fallbackRoute}
					/>
				}
				key={`guard-${route.key}`}
			>
				{routeElement}
			</Route>
		);
	});

export const getFirstAllowedRoute = (
	allRoutes: IRouteProps[],
	isRouteAllowed: (permission?: Permissions | Permissions[] | AnyOfPermissions) => boolean,
): string | undefined => {
	for (const route of allRoutes) {
		if (route.collapse) {
			const nested = getFirstAllowedRoute(route.collapse, isRouteAllowed);
			if (nested) return nested;
		}
		if (
			route.route &&
			route.protected &&
			route.key !== 'logout' &&
			isRoutePermitted(route, isRouteAllowed, getLicenseId())
		) {
			return route.route;
		}
	}
	return undefined;
};

export const DEVELOPMENT_LICENSE_ID = 'ae69b5d8-2486-43c9-b647-0e41af0204b5';

export function isDevelopmentAccountLicense() {
	return getLicenseId() === DEVELOPMENT_LICENSE_ID;
}

const SPORTS_PLAN_ALLOWED_KEYS = new Set(['devices', 'sports', 'config', 'logout']);

/**
 * Filtra a lista de rotas pra exibição no menu (sidebar/header), sem afetar o roteamento em si:
 * - Licença de desenvolvimento: fica fora da regra de plano Sports por completo, sempre vê o menu
 *   inteiro — usada internamente pra testar qualquer módulo.
 * - Fora do plano Sports: só tira a aba "Esportes", já que não faz sentido pra outros planos.
 * - No plano Sports: só deixa Dispositivos, Esportes e Configurações — o resto some do menu.
 * `hideInMenu` marca rotas que nunca devem ser afetadas por essa regra nem aparecer no loop
 * genérico de menu do SidebarComponent (ex: `logout`, que já tem botão fixo próprio no rodapé).
 */
export const filterRoutesByPlan = (
	allRoutes: IRouteProps[],
	isDevLicense: boolean,
	isSports: boolean,
): IRouteProps[] => {
	if (isDevLicense) {
		return allRoutes;
	}

	if (!isSports) {
		return allRoutes.filter((route) => route.key !== 'sports');
	}

	const filtered = allRoutes.filter((route) => {
		if (route.type === 'title' || route.hideInMenu) return true;
		return SPORTS_PLAN_ALLOWED_KEYS.has(route.key);
	});

	return filtered.filter((route, index) => {
		if (route.type !== 'title') return true;
		const next = filtered[index + 1];
		return next !== undefined && next.type !== 'title' && !next.hideInMenu;
	});
};
